/* eslint-disable react/prop-types */
import { useMemo } from "react";

import {
  alignItems,
  colors,
  darkModeColors,
  campaignStatus,
  strCode,
  campaignCategoriesOptions
} from "../../../../consts";
import { t } from "../../../../services";
import { appsUtil, dateUtil } from "../../../../utils";

import {
  FlexContainers,
  GradientLoader,
  Paragraph2,
  SVG,
  TableItemsCounter,
  TextOverflowWrapper,
  WrapperContainer
} from "../../../../components";
import { CellWrapper } from "../../../../components/Table/styled";
import { ActiveCampaignIconWrapper, EmptyCircle } from "./styled";

export const NotificationCampaignTableColumns = [
  {
    Header: strCode.NAME_TITLE,
    accessor: "campaignName",
    Cell: ({ cell, cellWidth8, isLoading, searchedText }) => {
      return (
        <CellWrapper width={`${cellWidth8}px`}>
          <TextOverflowWrapper
            highlightedText={searchedText}
            style={{
              color: darkModeColors.RAISIN_70
            }}>
            {isLoading ? (
              <GradientLoader width={160} height={24} animationDuration={24} />
            ) : (
              cell.value
            )}
          </TextOverflowWrapper>
        </CellWrapper>
      );
    }
  },
  {
    Header: strCode.CATEGORY_TITLE,
    accessor: "notificationType",
    Cell: ({ cell, cellWidth8, isLoading }) => {
      const eventTitle = useMemo(() => {
        return campaignCategoriesOptions.find((option) => option.id === cell.value);
      }, [cell.value]);

      return (
        <CellWrapper width={`${cellWidth8}px`}>
          <TextOverflowWrapper
            style={{
              color: darkModeColors.RAISIN_70
            }}>
            {isLoading ? (
              <GradientLoader width={160} height={24} animationDuration={24} />
            ) : (
              t(eventTitle?.value)
            )}
          </TextOverflowWrapper>
        </CellWrapper>
      );
    }
  },
  {
    Header: strCode.AUDIENCE_TITLE,
    accessor: "audience",
    Cell: ({ cell, cellWidth5, isLoading }) => {
      const audienceTypes = cell.value?.deviceTypes
        ? cell.value?.deviceTypes?.map((device) => device)
        : cell.value?.appTypes?.map((app) => appsUtil.getAppName(app));
      const audienceTypesCount = audienceTypes?.length || 0;

      return (
        <CellWrapper width={`${cellWidth5}px`}>
          <TextOverflowWrapper
            lines={2}
            style={{
              color: darkModeColors.RAISIN_70
            }}>
            {isLoading ? (
              <GradientLoader width={220} height={24} animationDuration={24} />
            ) : audienceTypesCount ? (
              <>
                {audienceTypesCount <= 3 ? (
                  audienceTypes.join(", ")
                ) : (
                  <>
                    {audienceTypes.slice(0, 3).join(", ")}
                    <TableItemsCounter appsCount={audienceTypesCount - 3} />
                  </>
                )}
              </>
            ) : null}
          </TextOverflowWrapper>
        </CellWrapper>
      );
    }
  },
  {
    Header: strCode.MARKET_TITLE,
    accessor: "markets",
    Cell: ({ cell, cellWidth10, isLoading }) => {
      const markets = cell?.value ?? [];

      return (
        <CellWrapper width={`${cellWidth10}px`}>
          <TextOverflowWrapper style={{ color: colors.RAISIN_70 }}>
            {isLoading ? (
              <GradientLoader width={60} height={24} animationDuration={40} />
            ) : (
              markets?.join(", ")
            )}
          </TextOverflowWrapper>
        </CellWrapper>
      );
    }
  },
  {
    Header: strCode.START_DATE_TITLE,
    accessor: "startTs",
    Cell: ({ cell, cellWidth8, isLoading }) => {
      let timestamp = null;

      try {
        const date = dateUtil.getUTCDate(new Date(cell?.value));
        timestamp = date?.getTime();
      } catch (e) {
        timestamp = null;
      }

      return (
        <CellWrapper width={`${cellWidth8}px`}>
          <TextOverflowWrapper style={{ color: colors.RAISIN_70 }}>
            {isLoading ? (
              <GradientLoader width={160} height={24} animationDuration={24} />
            ) : (
              dateUtil.formatDateFromTimestamp(timestamp)
            )}
          </TextOverflowWrapper>
        </CellWrapper>
      );
    }
  },
  {
    Header: strCode.PROGRESS_TITLE,
    accessor: "status",
    Cell: ({ cell, cellWidth5, isLoading }) => {
      const status = cell.value;
      const isActive = status === campaignStatus.active;
      const isInactive = status === campaignStatus.inactive;
      const isScheduled = status === campaignStatus.scheduled;
      const isDeactivated = status === campaignStatus.deactivated;

      return (
        <CellWrapper width={`${cellWidth5}px`}>
          {isLoading ? (
            <GradientLoader width={220} height={24} animationDuration={24} />
          ) : (
            <WrapperContainer Container={FlexContainers.FlexRowStart} gap={10}>
              {isDeactivated ? (
                <SVG.FailureFilledIcon fill={colors.RAISIN_40} />
              ) : isScheduled ? (
                <EmptyCircle />
              ) : isActive || isInactive ? (
                <ActiveCampaignIconWrapper>
                  <SVG.ArrowDownLeft />
                </ActiveCampaignIconWrapper>
              ) : (
                <>
                  <SVG.UncheckedCircle />
                  <Paragraph2>{t(strCode.SCHEDULED_TITLE)}</Paragraph2>
                </>
              )}

              <WrapperContainer
                Container={FlexContainers.FlexColumnStart}
                alignItems={alignItems.start}
                maxWidth={`${cellWidth5}px`}
                textAlign={"left"}>
                <TextOverflowWrapper
                  maxWidth={`${cellWidth5}px`}
                  style={{ color: colors.RAISIN_80 }}>
                  {t(
                    isDeactivated
                      ? strCode.DEACTIVATED_INFO
                      : isScheduled
                      ? strCode.SCHEDULED_TITLE
                      : isActive || isInactive
                      ? strCode.NOTIFICATION_OPENED_INFO
                      : "",
                    {
                      opened: cell.row.original.receivedNotifications,
                      total: +cell.row.original.sentNotifications // could be null if no users will receive notification
                    }
                  )}
                </TextOverflowWrapper>
              </WrapperContainer>
            </WrapperContainer>
          )}
        </CellWrapper>
      );
    }
  }
];
